import { useEffect, useState } from 'react'

export interface UseScrollDirectionOptions {
	/** Min scroll delta (px) to consider a direction change. Default 8. */
	threshold?: number
	/** When scroll Y is at or below this (px), always report visible. Default 16. */
	topThreshold?: number
	/** Scroll target. Default is window (document scroll). */
	scrollTarget?: Window | HTMLElement | null
}

/**
 * Returns whether the UI should be "visible" based on scroll direction:
 * - true when scrolling up or near top of page
 * - false when scrolling down
 *
 * Uses passive scroll listener on the given target (default: window).
 * Cleanup is handled in useEffect (idiomatic React for global events).
 */
export function useScrollDirection(
	options: UseScrollDirectionOptions = {}
): boolean {
	const {
		threshold = 8,
		topThreshold = 16,
		scrollTarget = typeof window !== 'undefined' ? window : null,
	} = options

	const getCurrentY = (target: Window | HTMLElement) =>
		'scrollY' in target ? target.scrollY : target.scrollTop

	const [visible, setVisible] = useState(() => {
		if (!scrollTarget) return true
		return getCurrentY(scrollTarget) <= topThreshold
	})

	useEffect(() => {
		if (!scrollTarget) return

		let lastY = getCurrentY(scrollTarget)
		// Sync on mount so late-mounted consumers reflect current scroll state.
		setVisible(lastY <= topThreshold)

		const handleScroll = () => {
			const currentY = getCurrentY(scrollTarget)
			const delta = currentY - lastY

			if (currentY <= topThreshold) {
				setVisible(true)
			} else if (delta > threshold) {
				setVisible(false)
			} else if (delta < -threshold) {
				setVisible(true)
			}

			lastY = currentY
		}

		scrollTarget.addEventListener('scroll', handleScroll, { passive: true })
		return () => scrollTarget.removeEventListener('scroll', handleScroll)
	}, [scrollTarget, threshold, topThreshold])

	return visible
}
